import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { GetRelatedProductCollectionApiV2Parameter, getRelatedProductsCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Props {
    apiParameter: GetRelatedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
}
type HandlerType = Handler<GetRelatedProductCollectionApiV2Parameter, Props>;

type CacheEntry = { items: ProductModel[]; total: number };
const bomCache = new Map<string, CacheEntry>();

const sanitize = (v: unknown) => String(v ?? "").replace(/[|¦]/g, "_").trim();
const buildCacheKey = (p: GetRelatedProductCollectionApiV2Parameter) =>
    `${sanitize(p.productId)}|${sanitize(p.relationship)}`;

const toNumber = (v: unknown) => {
    const n = typeof v === "string" ? Number(v.trim()) : Number(v);
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};
const getPosition = (p: ProductModel) =>
    toNumber(
        (p as any).properties?.Position ??
        (p as any).position ??
        (p as any).properties?.position ??
        (p as any).Position
    );

let requestToken = 0;

const setParams: HandlerType = props => {
    props.dispatch({ type: "BeginLoadBillToMaterialProducts" } as any);
    props.apiParameter = {
        ...props.apiParameter,
        productId: props.parameter.productId,
        relationship: props.parameter.relationship,
        page: props.parameter.page || 1,
        pageSize: props.parameter.pageSize,
        expand:["properties"]
    };
    requestToken += 1;
    (props as any)._requestToken = requestToken;
}
const getBillToMaterialProductsData: HandlerType = async props => {

    try {
        if (!("relationship" in props.apiParameter)) return;
        const firstPage = await getRelatedProductsCollectionV2(props.apiParameter);
        if ((props as any)._requestToken !== requestToken) return;

        const pagination = firstPage.pagination;
        const totalItemCount = pagination?.totalItemCount ?? (firstPage.products?.length ?? 0);

        if (!totalItemCount) {
            props.dispatch({
                type: "CompleteLoadBillToMaterialProducts",
                productId: props.parameter.productId,
                products: [],
                pagination,
            } as any);
            return;
        }

        const cacheKey = buildCacheKey(props.apiParameter);
        const cached = bomCache.get(cacheKey);
        let all = cached?.items;

        if (!all || (cached?.total ?? 0) !== totalItemCount) {
            const fullResp = await getRelatedProductsCollectionV2({
                ...props.apiParameter,
                page: 1,
                pageSize: totalItemCount,
            });
            if ((props as any)._requestToken !== requestToken) return;

            let collected: ProductModel[] = fullResp.products ?? [];

            if (collected.length < totalItemCount) {
                const pageSizeFromApi =
                    fullResp.pagination?.pageSize ||
                    firstPage.pagination?.pageSize ||
                    pagination?.pageSize ||
                    Math.max(1, firstPage.products?.length || 1);

                const totalPages = Math.max(1, Math.ceil(totalItemCount / pageSizeFromApi));
                const pages: number[] = [];
                for (let p = 2; p <= totalPages; p++) pages.push(p);

                const results = await Promise.all(
                    pages.map(p =>
                        getRelatedProductsCollectionV2({
                            ...props.apiParameter,
                            page: p,
                            pageSize: pageSizeFromApi,
                        })
                    )
                );
                if ((props as any)._requestToken !== requestToken) return;

                for (const resp of results) {
                    if (resp?.products?.length) {
                        collected.push(...resp.products);
                    }
                }
            }

            collected.sort((a, b) => getPosition(a) - getPosition(b));
            bomCache.set(cacheKey, { items: collected, total: collected.length });
            all = collected;
        }

        const requestedPage = props.apiParameter.page || 1;
        const requestedPageSize =
            props.apiParameter.pageSize ||
            pagination?.defaultPageSize ||
            pagination?.pageSize ||
            all.length;

        const start = (requestedPage - 1) * requestedPageSize;
        const end = start + requestedPageSize;
        const slice = all.slice(start, end);

        props.dispatch({
            type: "CompleteLoadBillToMaterialProducts",
            productId: props.parameter.productId,
            products: slice,
            pagination,
        } as any);
     } catch (error) {
        props.dispatch({ type: "FailedLoadBillToMaterialProducts" } as any);
        console.error("Failed to load related products", error);
    }
};
export const chain = [
    setParams,
    getBillToMaterialProductsData,
];

const loadBillToMaterialProducts = createHandlerChainRunner(chain, "LoadBillToMaterialProducts");
export default loadBillToMaterialProducts;
